/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {Base64} from 'js-base64';
import isNull from 'lodash.isnull';
import isObject from 'lodash.isobject';
import isString from 'lodash.isstring';
import {
  AlertAction,
  AlertActionTypes,
  EntityStatus,
  Feature,
  GenericObject,
  Notification
} from '@ion/core/src/types/entities';
import {isJSONDescription, safeJSONParse} from '@ion/core/src/utils/JSONUtils';

// defines the enum for alert action fields ...
export enum AlertActionFields {
  Account = 'account',
  Description = 'description',
  Status = 'status',
  Name = 'name',
  Email = 'primaryRecipientsEmail',
  SecondaryEmail = 'secondaryRecipientsEmail',
  Subject = 'emailSubject',
  Phone = 'primaryRecipientsPhone',
  ActionType = 'actionType',
  Destinations = 'destinations',
  EmailInfo = 'emailInfo',
  SMSInfo = 'smsInfo',
  URLInfo = 'urlInfo',
  MobileInfo = 'mobileInfo',
  EditParams = 'editParams',
  SwitchEmail = 'switchEmail',
  SwitchSms = 'switchSms',
  SwitchUrl = 'switchUrl',
  SwitchMobile = 'switchMobile',
  SMSTemplate = 'smsTemplate',
  EmailTemplate = 'emailTemplate',
  URLTemplate = 'urlTemplate',
  MobileTemplate = 'mobileTemplate',
  URL = 'url',
  Username = 'username',
  Password = 'password',
  AppName = 'appName',
  Platform = 'platform',
  DeviceIdentifier = 'deviceIdentifier'
};

export const POST_DESTRUCT_KEYS = [
  AlertActionFields.Email,
  AlertActionFields.Subject,
  AlertActionFields.SecondaryEmail,
  AlertActionFields.Phone,
  AlertActionFields.SMSInfo,
  AlertActionFields.EmailInfo,
  AlertActionFields.URLInfo,
  AlertActionFields.MobileInfo,
  AlertActionFields.EditParams,
  AlertActionFields.SwitchEmail,
  AlertActionFields.SwitchSms,
  AlertActionFields.SwitchUrl,
  AlertActionFields.SwitchMobile,
  AlertActionFields.EmailTemplate,
  AlertActionFields.SMSTemplate,
  AlertActionFields.URLTemplate,
  AlertActionFields.MobileTemplate,
  AlertActionFields.Username,
  AlertActionFields.Password,
  AlertActionFields.URL,
  AlertActionFields.AppName,
  AlertActionFields.Platform,
  AlertActionFields.DeviceIdentifier
];

// TPMS Alert Types (alert type names are not localized)
export const TPMS_TYPES = [
  'iOn Tire Pressure Excursion',
  'iOn Tire Temperature Excursion',
  'iOn Maintenance Due Alert',
  'iOn Maintenance Alert'
];

// iOn Tag Environmental Sensor types...
export const SC_DEVICE_TYPES = [
  'iOn Tag Environmental Sensor Breach',
  'iOn Gateway Stationary Alert',
  'iOn Gateway Environmental Sensor Breach'
];

// WETS Alert Types (alert type names are not localized)
const WETS_TYPES = [
  'iOn Hub Temperature Excursion'
];

// Door Sensor Alert Types (alert type names are not localized)
const DOOR_SENSOR_TYPES = [
  'iOn Door Status Alert'
];

export const destroyPostDesturct = (notification) => {
  POST_DESTRUCT_KEYS.forEach(key => {
    delete notification[key];
  });
  return notification;
};

const preparePhoneNumber = (notification, isCtcAdmin = false): string[] | null => {
  if (isCtcAdmin) {
    return notification[AlertActionFields.Phone]?.map((phoneNumber) => {
      return phoneNumber?.replace(/\s/g, '');
    });
  } else {
    return formatProperty(notification, AlertActionFields.Phone);
  }
};

// just extract the values from notification properties...
const extractValue = property => {
  return isObject(property) ? (property as any)?.value : property;
};

const formatValue = (value: string) => value?.replace(/\s/g, '')?.split?.(',') || null;

const formatProperty = (notification, field: AlertActionFields): string[] | null => {
  return formatValue(notification[field]);
};

// util function to destruct alert actions and alert details ...
export const destructAlertAction = (notification, isCtcAdmin = false): {
  alertDetails: Notification,
  alertActions: AlertAction[]
} => {
  const alertActions: AlertAction[] = [];
  let emailAlertAction: AlertAction = {
    [AlertActionFields.Status]     : EntityStatus.Enabled,
    [AlertActionFields.ActionType] : AlertActionTypes.EMAIL
  };
  let smsAlertAction: AlertAction = {
    [AlertActionFields.Status]     : EntityStatus.Enabled,
    [AlertActionFields.ActionType] : AlertActionTypes.SMS
  };
  let urlAlertAction: AlertAction = {
    [AlertActionFields.Status]     : EntityStatus.Enabled,
    [AlertActionFields.ActionType] : AlertActionTypes.URLPOST
  };
  let mobileAlertAction: AlertAction = {
    [AlertActionFields.Status]     : EntityStatus.Enabled,
    [AlertActionFields.ActionType] : AlertActionTypes.MOBILE_PUSH
  };

  if (notification[AlertActionFields.EmailInfo]) {
    const emailInfo = {...notification[AlertActionFields.EmailInfo]};
    delete emailInfo?.destinations;
    delete emailInfo?.alertActionTemplate;
    emailAlertAction = {...emailAlertAction, ...emailInfo};
  }
  if (notification[AlertActionFields.SwitchEmail]) {
    const emailTemplate  = notification[AlertActionFields.EmailTemplate];
    const primaryEmail   = extractValue(notification[AlertActionFields.Email]);
    const subject        = extractValue(notification[AlertActionFields.Subject]);
    const secondaryEmail = extractValue(notification[AlertActionFields.SecondaryEmail]);
    emailAlertAction[AlertActionFields.Destinations] = {
      email : {
        to      : formatValue(primaryEmail),
        cc      : formatValue(secondaryEmail),
        subject : isString(subject) ? Base64.encode(subject) : ''
      },
      sms        : null,
      http       : null,
      mobilePush : null
    };
    if (!isNull(emailTemplate)) {
      emailAlertAction.alertActionTemplate = emailTemplate;
    }
    alertActions.push(emailAlertAction);
  } else if (emailAlertAction?.id && !notification[AlertActionFields.SwitchEmail]) {
    emailAlertAction.status = EntityStatus.Deleted;
    alertActions.push(emailAlertAction);
  }

  if (notification[AlertActionFields.SMSInfo]) {
    const smsInfo = {...notification[AlertActionFields.SMSInfo]};
    delete smsInfo?.destinations;
    delete smsInfo?.alertActionTemplate;
    smsAlertAction = {...smsAlertAction, ...smsInfo};
  }
  if (notification[AlertActionFields.SwitchSms]) {
    const smsTemplate = notification[AlertActionFields.SMSTemplate];
    smsAlertAction[AlertActionFields.Destinations] = {
      sms : {
        to : preparePhoneNumber(notification, isCtcAdmin)
      },
      email      : null,
      http       : null,
      mobilePush : null
    };
    if (!isNull(smsTemplate)) {
      smsAlertAction.alertActionTemplate = smsTemplate;
    }
    alertActions.push(smsAlertAction);
  } else if (smsAlertAction?.id && !notification[AlertActionFields.SwitchSms]) {
    smsAlertAction.status = EntityStatus.Deleted;
    alertActions.push(smsAlertAction);
  }

  if (notification[AlertActionFields.URLInfo]) {
    const urlInfo = {...notification[AlertActionFields.URLInfo]};
    delete urlInfo?.destinations;
    delete urlInfo?.alertActionTemplate;
    urlAlertAction = {...urlAlertAction, ...urlInfo};
  }
  if (extractValue(notification[AlertActionFields.SwitchUrl])) {
    const urlTemplate = notification[AlertActionFields.URLTemplate];
    urlAlertAction[AlertActionFields.Destinations] = {
      http : {
        username : notification[AlertActionFields.Username],
        password : notification[AlertActionFields.Password],
        url      : notification[AlertActionFields.URL]
      },
      email      : null,
      sms        : null,
      mobilePush : null
    };
    if (!isNull(urlTemplate)) {
      urlAlertAction.alertActionTemplate = urlTemplate;
    }
    alertActions.push(urlAlertAction);
  } else if (urlAlertAction?.id && !notification[AlertActionFields.SwitchUrl]) {
    urlAlertAction.status = EntityStatus.Deleted;
    alertActions.push(urlAlertAction);
  }

  if (notification[AlertActionFields.MobileInfo]) {
    const MobileInfo = {...notification[AlertActionFields.MobileInfo]};
    delete MobileInfo?.destinations;
    delete MobileInfo?.alertActionTemplate;
    mobileAlertAction = {...mobileAlertAction, ...MobileInfo};
  }
  if (extractValue(notification[AlertActionFields.SwitchMobile])) {
    const mobileTemplate = notification[AlertActionFields.MobileTemplate];
    const deviceIdentifiers = notification[AlertActionFields.DeviceIdentifier]?.split(',');
    let trimmedDevices = deviceIdentifiers?.map(deviceIdentifier => deviceIdentifier.trim());
    trimmedDevices = trimmedDevices.filter((deviceIdentifier) => deviceIdentifier !== '');
    mobileAlertAction[AlertActionFields.Destinations] = {
      mobilePush : {
        appName  : notification[AlertActionFields.AppName],
        audience : [{
          platform               : notification[AlertActionFields.Platform],
          deviceIdentifierTokens : trimmedDevices
        }]
      },
      email : null,
      sms   : null,
      http  : null
    };
    if (!isNull(mobileTemplate)) {
      mobileAlertAction.alertActionTemplate = mobileTemplate;
    }
    alertActions.push(mobileAlertAction);
  } else if (mobileAlertAction?.id && !notification[AlertActionFields.SwitchMobile]) {
    mobileAlertAction.status = EntityStatus.Deleted;
    alertActions.push(mobileAlertAction);
  }

  return {alertDetails: destroyPostDesturct(notification), alertActions};
};

// If the current user does not have certain applications enabled
// (currently only TPMS), we need to omit the related alert types
// in the editor's notification type chooser.
export const getTypesToOmit = (Features) => {
  let typesToOmit = [];
  if (!Features.hasSmartTrailer()) {
    typesToOmit = [...typesToOmit, ...TPMS_TYPES];
    typesToOmit = [...typesToOmit, ...WETS_TYPES];
    typesToOmit = [...typesToOmit, ...DOOR_SENSOR_TYPES];
  }
  if (!Features.hasSCIAccess()) {
    typesToOmit = [...typesToOmit, ...SC_DEVICE_TYPES];
  }
  return typesToOmit;
};

/**
 * get permitted notification type for the user based on respose from notification API response
 * and predefined types to omit
 *
 * ```typescript
 *   import {getPermittedType} from '@ion/application/src/pages/admin/notifications/NotificationUtils.ts';
 *
 *   const results = [
 *     {
 *       'id': 20657,
 *       'name': 'iOn Gateway Environmental Sensor Breach'
 *     },
 *     {
 *       'id': 20673,
 *       'name': 'iOn Gateway Stationary Alert'
 *     },
 *     {
 *       'id': 21202,
 *       'name': 'iOn Gateway Battery Level Alert'
 *     }
 *    ];
 *
 *   const typesToOmit = [
 *       'iOn Tag Environmental Sensor Breach',
 *       'iOn Gateway Stationary Alert',
 *       'iOn Gateway Environmental Sensor Breach'
 *     ];
 *
 *   const permittedTypes =  getPermittedType(results, Features, typesToOmit);
 *   // [{'id': 21202, 'name': 'iOn Gateway Battery Level Alert'}]
 * ```
 */
export const getPermittedType = (results: GenericObject[], Features: Feature, typesToOmit: string[] = []) => {
  const notificationResponse = [...results];
  let notificationTypesList = notificationResponse?.sort((a, b) => a.name > b.name ? 1 : -1);
  notificationTypesList = notificationTypesList?.filter(({name, description}) => {
    let hasPermission = true;
    const json = isJSONDescription(description) ? safeJSONParse(description) as GenericObject : null;
    if (Features && json) {
      const {permissions} = json;
      permissions?.forEach?.(({id}) => {
        hasPermission &&= Features?.has(id);
      });
    }
    return hasPermission && !typesToOmit?.includes(name);
  });
  return notificationTypesList;
};

// hide "VIEW EVENT" for TPMS Alert Types
export const hasHideViewEventCTA = (alertTypeTitle: string): boolean => {
  const title = alertTypeTitle.toLowerCase();
  return TPMS_TYPES.some(alertType => alertType.toLowerCase() === title);
};
